import { useEffect, useState } from 'react';
import api from '../../services/api';
import AccessWarningModal from '../common/AccessWarningModal';

function AccessControlPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(null);

  const loadRequests = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await api.getMyAccessRequests();
      setRequests(data);
    } catch (err) {
      setError(err.message || 'Не удалось загрузить доступы');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRequests();
  }, []);

  const openApprove = (request) => {
    setDialog({
      type: 'approve',
      request,
      title: 'Одобрить запрос доступа',
      warning:
        'Психолог получит доступ к материалам вашей истории в рамках выбранной области (например, результаты тестов). Убедитесь, что вы доверяете этому специалисту.',
      confirmLabel: 'Одобрить доступ',
      confirmButtonClass: 'btn-success',
      showOptionalMessage: true,
      optionalMessageLabel: 'Комментарий для психолога (необязательно)',
    });
  };

  const openReject = (request) => {
    setDialog({
      type: 'reject',
      request,
      title: 'Отклонить запрос доступа',
      warning:
        'Психолог не получит доступ к вашим материалам. При необходимости вы сможете рассмотреть новый запрос позже.',
      confirmLabel: 'Отклонить',
      confirmButtonClass: 'btn-outline-danger',
      showOptionalMessage: true,
      optionalMessageLabel: 'Комментарий для психолога (необязательно)',
    });
  };

  const openRevoke = (request) => {
    setDialog({
      type: 'revoke',
      request,
      title: 'Отозвать доступ',
      warning:
        'Доступ будет немедленно прекращён: психолог перестанет видеть материалы, для которых был выдан доступ. Это действие нельзя отменить автоматически — потребуется новый запрос от психолога и ваше решение.',
      confirmLabel: 'Отозвать доступ',
      confirmButtonClass: 'btn-danger',
      reasonRequired: true,
      reasonLabel: 'Причина отзыва',
      reasonPlaceholder: 'Кратко опишите причину — её увидит психолог в контексте доступа.',
    });
  };

  const handleModalConfirm = async ({ reason, optionalMessage }) => {
    const { type, request } = dialog;
    if (type === 'approve') {
      await api.approveAccessRequest(request._id, optionalMessage);
    } else if (type === 'reject') {
      await api.rejectAccessRequest(request._id, optionalMessage);
    } else if (type === 'revoke') {
      await api.revokeAccessRequest(request._id, reason);
    }
    await loadRequests();
  };

  return (
    <div className="container mt-4">
      <h2 className="mb-3">Кто имеет доступ</h2>
      <p className="text-muted">Управление доступом психологов к материалам вашей истории.</p>

      {loading && <div className="alert alert-info">Загрузка доступов...</div>}
      {error && <div className="alert alert-danger">{error}</div>}

      {!loading && !error && (
        <div className="ui-card p-3">
          {requests.length === 0 ? (
            <div className="alert alert-secondary mb-0">Запросов и активных доступов пока нет.</div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead>
                  <tr>
                    <th>Психолог</th>
                    <th>Статус</th>
                    <th>Запрошено</th>
                    <th>Закрытие</th>
                    <th>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.map((request) => (
                    <tr key={request._id}>
                      <td>
                        <strong>{request.psychologistId?.username || 'Неизвестно'}</strong>
                        <div className="text-muted small">
                          {request.psychologistId?.firstName || ''} {request.psychologistId?.lastName || ''}
                        </div>
                      </td>
                      <td>
                        <span className="badge bg-secondary">{request.status}</span>
                      </td>
                      <td>{request.requestedAt ? new Date(request.requestedAt).toLocaleString('ru-RU') : '—'}</td>
                      <td>
                        {request.closedAt ? new Date(request.closedAt).toLocaleString('ru-RU') : '—'}
                        {request.closeReason && <div className="text-muted small">{request.closeReason}</div>}
                      </td>
                      <td className="d-flex gap-2 flex-wrap">
                        {request.status === 'pending' && (
                          <>
                            <button type="button" className="btn btn-sm btn-outline-success" onClick={() => openApprove(request)}>
                              Одобрить
                            </button>
                            <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => openReject(request)}>
                              Отклонить
                            </button>
                          </>
                        )}
                        {request.status === 'approved' && (
                          <button type="button" className="btn btn-sm btn-danger" onClick={() => openRevoke(request)}>
                            Отозвать доступ
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <AccessWarningModal
        open={Boolean(dialog)}
        onClose={() => setDialog(null)}
        title={dialog?.title || ''}
        warning={dialog?.warning || ''}
        reasonRequired={dialog?.reasonRequired || false}
        reasonLabel={dialog?.reasonLabel}
        reasonPlaceholder={dialog?.reasonPlaceholder}
        showOptionalMessage={dialog?.showOptionalMessage || false}
        optionalMessageLabel={dialog?.optionalMessageLabel}
        confirmLabel={dialog?.confirmLabel || 'Подтвердить'}
        confirmButtonClass={dialog?.confirmButtonClass || 'btn-primary'}
        onConfirm={handleModalConfirm}
      />
    </div>
  );
}

export default AccessControlPage;
